/**
 * Gulf display currencies with the Nova Multi Currency Converter app (snippets/fd-currency.liquid).
 *
 * `window.FdCurrency` (set inline in <head>) holds the display currency of the Shopify country, the store currency, the
 * country and the available countries; the inline script already stored the currency where Nova reads it
 * (sessionStorage `bacurr_user_cur`), so Nova starts on it. This file:
 *
 * - keeps Nova on that currency once it has loaded (re-running its conversion if it ended up elsewhere);
 * - re-runs Nova's conversion when prices are re-rendered without it (cart drawer and cart page changes, variant
 *   changes, quick add): any added `span.money` that Nova has not marked `bacurr-money` yet, and any converted one that
 *   a re-render rewrote in place. Horizon's morph keeps a price's element, copies the server's attributes over it (so
 *   `class="money bacurr-money"` becomes `class="money"`) and rewrites its text to the store currency; no element is
 *   added. Losing the mark is that rewrite's signature: the price is swapped for a copy of itself (a new element, which
 *   already holds the new amount) before Nova runs again;
 * - on a visitor's first visit, asks Shopify where they are (/browsing_context_suggestions.json) and, if that is
 *   another available country, submits the region picker's localization form for it once. A choice the visitor
 *   makes first wins, and without storage the lookup never runs (no reload loop).
 *
 * Checkout always charges the store currency; the cart drawer says so.
 */
(() => {
  const config = window.FdCurrency;
  if (!config) return;

  const NOVA_KEY = 'bacurr_user_cur';
  const GEO_KEY = 'fd-region-geo';

  const nova = () => {
    const app = /** @type {any} */ (window).baCurr;
    return app && app.config && typeof app.refreshConversion === 'function' ? app : null;
  };

  const refresh = () => {
    try {
      nova()?.refreshConversion();
    } catch (error) {
      // Nova's own failure must not break the page.
    }
  };

  // ---- Keep Nova on the Shopify country's currency ----
  const keepCurrency = () => {
    const app = nova();
    if (!app) return false;
    let stored = null;
    try {
      stored = JSON.parse(sessionStorage.getItem(NOVA_KEY) || 'null');
    } catch (error) {}
    if (stored !== config.currency) {
      try {
        sessionStorage.setItem(NOVA_KEY, JSON.stringify(config.currency));
      } catch (error) {}
      refresh();
    }
    return true;
  };

  // Nova loads deferred through its app embed; wait for it (up to 15 s).
  let tries = 0;
  const waitForNova = () => {
    if (keepCurrency() || ++tries > 60) return;
    setTimeout(waitForNova, 250);
  };
  waitForNova();

  // ---- Re-convert re-rendered prices ----
  if (config.currency !== config.store) {
    const MARK = 'bacurr-money';
    /** @type {number | undefined} */
    let pending;
    /** @param {Node} node */
    const hasNewPrice = (node) =>
      node instanceof Element &&
      ((node.matches('span.money') && !node.classList.contains(MARK)) ||
        !!node.querySelector(`span.money:not(.${MARK})`));
    /** A converted price whose mark a re-render just took away (its text is the store currency again). */
    const lostMark = (/** @type {MutationRecord} */ record) => {
      const el = record.target;
      return (
        el instanceof Element &&
        el.isConnected &&
        el.matches('span.money') &&
        !el.classList.contains(MARK) &&
        (record.oldValue || '').split(/\s+/).includes(MARK)
      );
    };
    const observer = new MutationObserver((records) => {
      if (!nova()) return;
      let found = false;
      for (const record of records) {
        if (record.type === 'attributes') {
          if (!lostMark(record)) continue;
          const el = /** @type {Element} */ (record.target);
          el.replaceWith(el.cloneNode(true));
          found = true;
        } else if (!found && [...record.addedNodes].some(hasNewPrice)) {
          found = true;
        }
      }
      if (!found || pending !== undefined) return;
      pending = window.setTimeout(() => {
        pending = undefined;
        refresh();
        // Nova's own changes (its mark added, the converted text) need no answer.
        observer.takeRecords();
      }, 150);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true,
    });
  }

  // ---- First visit: move a visitor from another available country there, once ----
  let seen = null;
  try {
    seen = localStorage.getItem(GEO_KEY);
    localStorage.setItem(GEO_KEY, '1');
  } catch (error) {
    return;
  }
  if (seen) return;

  let chose = false;
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (form instanceof HTMLFormElement && form.querySelector('[name="country_code"]')) chose = true;
    },
    true
  );

  fetch('/browsing_context_suggestions.json', { credentials: 'same-origin' })
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => {
      const code = data?.detected_values?.country?.handle;
      if (chose || !code || code === config.country || !config.countries.includes(code)) return;
      const form = document.getElementById('FdRegionCountryForm');
      if (!(form instanceof HTMLFormElement)) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'country_code';
      input.value = code;
      form.append(input);
      form.submit();
    })
    .catch(() => {});
})();
